import { useEffect, useRef } from 'react';
import type { LoadBiOpts } from './use-generic-bi';
import { useGenericBi } from './use-generic-bi';

// A react-query-shaped async state. Pass the flags from a `useQuery`/data hook:
// `isLoading` should mean "a fetch the user is waiting on is in flight" (typically
// the hook's `isLoading`/`isPending`, optionally `|| isFetching` to also track refetches).
export type LoadBiState = {
  isLoading: boolean;
  isError?: boolean;
};

const now = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

/**
 * Fires the generic **src:198 loadStart (evid 4) / loadFinish (evid 5)** pair for a
 * component's async data region, driven off a react-query-shaped `state`.
 *
 * - `loadStart` fires when a fetch begins (the loading flag goes false → true, or is
 *   already true on mount).
 * - `loadFinish` fires when it settles, carrying the **real** elapsed time
 *   (`loadingTimeMs`) and the **true** `isSuccessful` (`!isError`).
 *
 * These are pure src:198 (no src:172 shadow) — component-level, mirroring the native
 * apps. A region served entirely from cache (never enters the loading state) emits
 * nothing, so the events count genuine loads only.
 *
 * @example
 * const q = useQuery(...);
 * useLoadBi('agents-list-data', { isLoading: q.isLoading, isError: q.isError });
 */
export const useLoadBi = (
  componentName: string,
  state: LoadBiState,
  opts?: LoadBiOpts & { enabled?: boolean; componentParentName?: string },
) => {
  const { logLoadStart, logLoadFinish } = useGenericBi(opts?.componentParentName);
  const startedAtRef = useRef<number | null>(null);
  const wasLoadingRef = useRef(false);

  // Keep the latest entity/additionalFields without making the effect re-fire on
  // every render (they are fresh object identities each render).
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const enabled = opts?.enabled ?? true;

  useEffect(() => {
    if (!enabled) return;
    const { entity, additionalFields } = optsRef.current ?? {};
    const isLoading = state.isLoading;
    const wasLoading = wasLoadingRef.current;

    if (isLoading && !wasLoading) {
      startedAtRef.current = now();
      logLoadStart(componentName, { entity, additionalFields });
    } else if (!isLoading && wasLoading) {
      const started = startedAtRef.current;
      const loadingTimeMs = started != null ? Math.round(now() - started) : 0;
      startedAtRef.current = null;
      logLoadFinish(
        componentName,
        { isSuccessful: !state.isError, loadingTimeMs },
        { entity, additionalFields },
      );
    }
    wasLoadingRef.current = isLoading;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, componentName, state.isLoading, state.isError]);
};
